/**
 * The decision layer of the "Repair cloud copy" action.
 *
 * WHY THIS IS A MODULE AND NOT INLINE JSX
 * --------------------------------------
 * Two rules here are the whole feature, and both are easy to lose in a
 * refactor of a 700-line screen:
 *
 *   1. A repair may not start twice. `busy === null` is the only guard, and it
 *      has to be, because `acquireSyncLock` is NOT re-entrant: wrapping the
 *      repair in it would deadlock the `SyncEngine.run()` it performs.
 *
 *   2. Success is only ever a verified cloud row. `CloudRepairOutcome.changed`
 *      means the local outbox entry was reset — one step BEFORE an upload that
 *      can still be rejected. Treating that as success is exactly what kept the
 *      pruned-business incident invisible, so the mapping from outcome to
 *      message lives here where it can be tested without a DOM.
 */
import type { CloudRepairOutcome } from '@data/sync/cloudRestore';
import type { SyncRunResult } from '@data/sync/SyncEngine';

/** Why a repair may or may not start. */
export type RepairGate = 'ready' | 'busy' | 'no_business' | 'signed_out' | 'not_authorised';

export function repairGate(input: {
  readonly businessId: string | null | undefined;
  readonly busy: string | null;
  readonly signedIn: boolean;
  readonly authorised: boolean;
}): RepairGate {
  // Busy first: a second click while one repair is running must be ignored
  // before anything else is even considered.
  if (input.busy !== null) return 'busy';
  if (!input.businessId) return 'no_business';
  if (!input.signedIn) return 'signed_out';
  if (!input.authorised) return 'not_authorised';
  return 'ready';
}

/** What a repair is allowed to tell the user. */
export type RepairVerdict =
  | { readonly kind: 'refused'; readonly code: string; readonly message: string }
  | { readonly kind: 'push_failed'; readonly message: string }
  | { readonly kind: 'unverified'; readonly message: string }
  | { readonly kind: 'deferred' }
  | { readonly kind: 'still_missing' }
  | { readonly kind: 'repaired' }
  | { readonly kind: 'already_present' };

export function repairVerdict(repair: CloudRepairOutcome | undefined): RepairVerdict {
  if (!repair) {
    return { kind: 'unverified', message: 'The repair produced no outcome.' };
  }
  if (repair.error) {
    return { kind: 'refused', code: repair.error.code, message: repair.error.message };
  }
  if (repair.pushError) {
    return { kind: 'push_failed', message: repair.pushError };
  }
  if (repair.serverHadBusiness) {
    // The pre-push check already confirmed the row, so nothing was reset and no
    // second check was made. A `false` can only come from a check that ran
    // anyway, and it wins: the cloud is the authority.
    return repair.cloudHasBusiness === false
      ? { kind: 'still_missing' }
      : { kind: 'already_present' };
  }
  if (repair.cloudHasBusiness === true) return { kind: 'repaired' };
  if (repair.cloudHasBusiness === false) {
    // The row is not there — but WHY decides the advice. An entry still in
    // `pending` was never accepted: the provider deferred it (a retryable
    // transport or server condition), or the cycle never reached the push. The
    // business is not missing from the cloud; the upload has not happened yet,
    // and the next sync owns it. Only an `acked` entry the cloud still cannot
    // show is the alarming state, and only that one deserves "contact support".
    return repair.entryStatus === 'pending'
      ? { kind: 'deferred' }
      : { kind: 'still_missing' };
  }
  // Nothing established that the upload was accepted: the entry may still be
  // `pending` (a retryable push failure, or a cycle that never reached the
  // push), and the check that would have settled it did not run. Say only what
  // is known.
  return {
    kind: 'unverified',
    message:
      'The cloud copy could not be verified, so the upload is not confirmed. Run Sync now, then repair again.',
  };
}

/**
 * What the business gate says after a restore attempt.
 *
 * WHY THE GATE NEEDS THIS AT ALL
 * ------------------------------
 * `restoreCloudBusiness` has two branches. The snapshot branch throws on every
 * failure, so `catch` is enough. The already-local branch does not: a refusal
 * comes back as a VALUE with `repair.error` set, because there is nothing to
 * throw — the business is fine, only the cloud copy could not be established.
 * The gate used to read `result.applied`, which is 0 on that branch, and
 * announce "Business restored and up to date" with a success tone for a
 * recovery that had just been refused. Reading the repair outcome is the
 * difference between a report and a reassurance.
 */
export interface GateRestoreMessage {
  /** False when the result must NOT be presented as a success. */
  readonly ok: boolean;
  readonly tone: 'success' | 'warning' | 'danger';
  readonly title: string;
  readonly description: string;
}

export function gateRestoreMessage(result: {
  readonly applied: number;
  readonly repair?: CloudRepairOutcome;
}): GateRestoreMessage {
  const repair = result.repair;

  // No `repair` means the snapshot branch ran: the business did not exist here
  // and has just been rebuilt from the cloud. There is nothing to verify beyond
  // what that branch already did.
  if (!repair) {
    const applied = result.applied;
    return {
      ok: true,
      tone: 'success',
      title:
        applied > 0
          ? `Business restored. ${applied} ${applied === 1 ? 'record' : 'records'} applied.`
          : 'Business restored and up to date.',
      description: 'Your business was restored from DNR Cloud Sync on this device.',
    };
  }

  const verdict = repairVerdict(repair);
  switch (verdict.kind) {
    case 'refused':
      return {
        ok: false,
        tone: 'danger',
        title: 'This business is on this device, but the cloud copy could not be prepared.',
        description: `Recovery refused (${verdict.code}): ${verdict.message}`,
      };
    case 'push_failed':
      return {
        ok: false,
        tone: 'danger',
        title: 'This business is on this device, but it was not uploaded.',
        description: `${verdict.message} Use "Retry failed changes" in Settings → DNR Cloud Sync, then try again.`,
      };
    case 'deferred':
      return {
        ok: false,
        tone: 'warning',
        title: 'This business is on this device. Its upload is waiting for the next sync.',
        description:
          'The cloud has no business row yet because the upload was deferred, not rejected. Open Settings → DNR Cloud Sync and use Sync now, then try again.',
      };
    case 'still_missing':
      return {
        ok: false,
        tone: 'danger',
        title: 'This business is on this device, but the cloud still has no copy.',
        description:
          'The server accepted the upload, yet the cloud snapshot still has no business row. Use Sync now, then try again; if it persists, contact support.',
      };
    case 'unverified':
      return {
        ok: false,
        tone: 'warning',
        title: 'This business is on this device. Its cloud copy could not be confirmed.',
        description: verdict.message,
      };
    case 'repaired':
      return {
        ok: true,
        tone: 'success',
        title: 'Business restored and republished to the cloud.',
        description: 'The cloud copy was missing, and the upload has been verified.',
      };
    case 'already_present':
      return {
        ok: true,
        tone: 'success',
        title: 'Business restored. The cloud copy was already up to date.',
        description: 'Nothing needed to be uploaded.',
      };
  }
}

/**
 * Changes the repair cycle carried that were NOT the business row itself.
 *
 * `SyncEngine.run()` drains the whole outbox and applies the whole pull, so a
 * repair always does more than republish one entity. Saying so is not
 * politeness: a silent "repaired" alongside a permanently rejected invoice is a
 * report the user cannot act on.
 */
export function repairSideEffects(repair: CloudRepairOutcome): string {
  const parts: string[] = [];
  if (repair.pushed > 1) parts.push(`${repair.pushed - 1} other change(s) uploaded`);
  if (repair.failed > 0) parts.push(`${repair.failed} change(s) permanently rejected`);
  if (repair.applied > 0) parts.push(`${repair.applied} row(s) applied from the cloud`);
  if (repair.conflicts > 0) parts.push(`${repair.conflicts} conflict(s) need review`);
  return parts.length === 0 ? '' : ` ${parts.join(', ')}.`;
}

/**
 * What one sync cycle did, in the terms the person cares about.
 *
 * WHY THE FIRST TWO WORDS ARE THE WHOLE FUNCTION
 * ----------------------------------------------
 * "Sync finished" is only true when nothing was left behind. A run that still
 * has queued work, a permanent rejection or an error has NOT finished, and
 * calling it finished is how a half-synced device comes to look healthy — the
 * same failure the status model was rebuilt around. The counts below can all be
 * non-zero on a cycle the engine completed, so the verdict is decided by the
 * status, not by the numbers.
 */
export function runSummary(result: SyncRunResult): string {
  const parts = [`${result.pushed} change(s) sent`, `${result.applied} applied`];
  if (result.failed > 0) parts.push(`${result.failed} permanently failed`);
  if (result.status.pendingCount > 0) parts.push(`${result.status.pendingCount} still pending`);
  if (result.conflicts > 0) parts.push(`${result.conflicts} conflict(s) found`);
  if (result.skipped > 0) parts.push(`${result.skipped} unreadable cloud change(s) skipped`);
  if (result.recovered) parts.push('recovered from a snapshot');
  const complete = result.status.kind !== 'error' && result.status.pendingCount === 0;
  // A skipped change is not pending — retrying will not help — but it is also
  // not a clean run: this device is knowingly missing a record. Saying so with
  // a third verdict is the difference between a report and a reassurance.
  const verdict = !complete
    ? 'Sync incomplete'
    : result.skipped > 0
      ? 'Sync finished with warnings'
      : 'Sync finished';
  return `${verdict}: ${parts.join(', ')}.`;
}
